import { useEffect, useMemo, useState } from 'react'
import { ConfirmDialog, Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { clearFiscalMemory } from './accountingFiscalMemoryService'
import type {
  FiscalMemoryClearResult,
  FiscalMemoryClearScope,
  FiscalMemoryImport,
} from './accountingFiscalMemoryTypes'

interface Props {
  unitId: string | null
  clientName: string
  imports: FiscalMemoryImport[]
  presetImportId?: string | null
  periodOnly?: boolean
  isOpen: boolean
  onClose: () => void
  onCleared: () => void
}

const INPUT_CLASS = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white'

export function FiscalMemoryClearModal({
  unitId,
  clientName,
  imports,
  presetImportId = null,
  periodOnly = false,
  isOpen,
  onClose,
  onCleared,
}: Props) {
  const { t, locale } = useTranslation()
  const [scope, setScope] = useState<FiscalMemoryClearScope>('period')
  const [importId, setImportId] = useState('')
  const [cutoffDate, setCutoffDate] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<FiscalMemoryClearResult | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setScope('period')
    setImportId(presetImportId || imports[0]?.id || '')
    setCutoffDate('')
    setConfirming(false)
    setBusy(false)
    setError(null)
    setResult(null)
  }, [isOpen, presetImportId])

  const selectedImport = useMemo(
    () => imports.find((memoryImport) => memoryImport.id === importId) || null,
    [importId, imports],
  )
  const formatDate = (value: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(`${value.slice(0, 10)}T12:00:00`))
    : '—'
  const periodLabel = (memoryImport: FiscalMemoryImport) => (
    `${formatDate(memoryImport.period_start)} – ${formatDate(memoryImport.period_end)}`
  )
  const canReview = scope === 'all'
    || (scope === 'period' && Boolean(selectedImport))
    || (scope === 'before' && Boolean(cutoffDate))

  const translatedError = (value: unknown) => {
    const code = String(value instanceof Error ? value.message : (value as any)?.message || '').trim()
    const translated = t(`accounting.fiscalMemory.errors.${code || 'clearFailed'}`)
    return translated.startsWith('accounting.')
      ? t('accounting.fiscalMemory.errors.clearFailed')
      : translated
  }
  const confirmationMessage = scope === 'period'
    ? t('accounting.fiscalMemory.clear.confirmPeriod', { period: selectedImport ? periodLabel(selectedImport) : '' })
    : scope === 'before'
      ? t('accounting.fiscalMemory.clear.confirmBefore', { date: formatDate(cutoffDate) })
      : t('accounting.fiscalMemory.clear.confirmAll', { client: clientName })

  const review = () => {
    if (!canReview || busy) return
    setError(null)
    setConfirming(true)
  }
  const execute = async () => {
    if (!unitId || busy) return
    setBusy(true)
    setError(null)
    try {
      const cleared = await clearFiscalMemory(unitId, scope, {
        importId: scope === 'period' ? importId : null,
        cutoffDate: scope === 'before' ? cutoffDate : null,
      })
      setResult(cleared)
      onCleared()
    } catch (clearError) {
      console.error('[FiscalMemoryClearModal] Clear error:', clearError)
      setError(translatedError(clearError))
    } finally {
      setBusy(false)
    }
  }
  const handleClose = () => {
    if (!busy) onClose()
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t(periodOnly ? 'accounting.fiscalMemory.clear.periodTitle' : 'accounting.fiscalMemory.clear.title')}
        size="sm"
        onSubmit={result ? undefined : review}
        submitLabel={t('accounting.fiscalMemory.clear.review')}
        submitting={busy || !canReview}
        closeLabel={t('actions.close')}
      >
        {result ? (
          <div className="py-5 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m5 13 4 4L19 7" /></svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">{t('accounting.fiscalMemory.clear.completed')}</h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.clear.completedSummary', {
              periods: String(result.import_count),
              products: String(result.affected_products),
              evidence: String(result.evidence_count + result.tax_evidence_count),
            })}</p>
            <button type="button" onClick={handleClose} className="mt-5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">{t('actions.close')}</button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <p className="font-semibold">{t('accounting.fiscalMemory.clear.warningTitle')}</p>
              <p className="mt-1 text-xs leading-5">{t('accounting.fiscalMemory.clear.warningHint')}</p>
            </div>
            {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">{error}</div>}
            {!periodOnly && <fieldset className="space-y-2">
              <legend className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">{t('accounting.fiscalMemory.clear.scope')}</legend>
              {(['period', 'before', 'all'] as const).map((option) => (
                <label key={option} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${scope === option ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' : 'border-slate-200 dark:border-slate-700'}`}>
                  <input type="radio" name="memory-clear-scope" value={option} checked={scope === option} onChange={() => { setScope(option); setError(null) }} className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500" />
                  <span><span className="block text-sm font-semibold text-slate-800 dark:text-slate-200">{t(`accounting.fiscalMemory.clear.scopes.${option}.title`)}</span><span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{t(`accounting.fiscalMemory.clear.scopes.${option}.hint`)}</span></span>
                </label>
              ))}
            </fieldset>}
            {periodOnly && selectedImport && <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900"><p className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.clear.period')}</p><p className="mt-1 font-semibold text-slate-900 dark:text-white">{periodLabel(selectedImport)}</p></div>}
            {!periodOnly && scope === 'period' && (
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('accounting.fiscalMemory.clear.period')}
                <select value={importId} onChange={(event) => setImportId(event.target.value)} className={`${INPUT_CLASS} mt-1`}>
                  {imports.map((memoryImport) => <option key={memoryImport.id} value={memoryImport.id}>{periodLabel(memoryImport)} · {memoryImport.source_document_name}</option>)}
                </select>
              </label>
            )}
            {scope === 'before' && (
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('accounting.fiscalMemory.clear.cutoffDate')}
                <input type="date" value={cutoffDate} onChange={(event) => setCutoffDate(event.target.value)} className={`${INPUT_CLASS} mt-1`} />
                <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.clear.cutoffHint')}</span>
              </label>
            )}
          </div>
        )}
      </Modal>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void execute()}
        title={t('accounting.fiscalMemory.clear.confirmTitle')}
        message={confirmationMessage}
        confirmText={t('accounting.fiscalMemory.clear.confirm')}
        cancelText={t('actions.cancel')}
        type="danger"
      />
    </>
  )
}
